import { useEffect, useRef, useState } from "react";
import { BadgeCheck, Camera, Loader2, ShieldCheck, Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, checkoutCart } from "../lib/api";
import { klaviyoIdentify } from "../lib/klaviyo";

export default function VerificationModal({ open, onClose, items }) {
  const [name, setName] = useState(() => localStorage.getItem("fd_verify_name") || "");
  const [email, setEmail] = useState(() => localStorage.getItem("fd_verify_email") || "");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [alreadyVerified, setAlreadyVerified] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setAlreadyVerified(false);
    setFile(null);
    setPreview(null);
    const saved = localStorage.getItem("fd_verify_email");
    if (saved) {
      api
        .get("/verification/status", { params: { email: saved } })
        .then((r) => {
          if (r.data.verified) {
            setAlreadyVerified(true);
            setEmail(saved);
            setName(r.data.full_name || localStorage.getItem("fd_verify_name") || "");
          }
        })
        .catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    const em = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) return;
    const t = setTimeout(() => {
      api
        .get("/verification/status", { params: { email: em } })
        .then((r) => setAlreadyVerified(!!r.data.verified))
        .catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [email]);

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    if (!alreadyVerified && !file) {
      toast.error("Please upload a photo of your ID.");
      return;
    }
    setBusy(true);
    try {
      let idPath = null;
      if (!alreadyVerified && file) {
        const form = new FormData();
        form.append("file", file);
        const up = await api.post("/verification/upload", form);
        idPath = up.data.path;
      }
      const res = await checkoutCart(
        items.map((i) => ({ product_id: i.product_id, qty: i.qty, option: i.option })),
        { full_name: name.trim(), email: email.trim(), id_path: idPath }
      );
      if (res.status === "ready") {
        klaviyoIdentify(email, name);
        localStorage.setItem("fd_verify_email", email.trim());
        localStorage.setItem("fd_verify_name", name.trim());
        toast.success("Verified — taking you to secure checkout.");
        window.location.href = "/checkout";
      } else {
        toast.info(res.message || "Checkout is being connected.");
        setBusy(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="verification-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl font-black tracking-tight">
            <ShieldCheck className="h-5 w-5 text-brand-red" /> Quick 21+ Verification
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm leading-relaxed text-neutral-500">
          One quick step before secure checkout — verify once and you won't be asked again on future orders.
        </p>
        <form onSubmit={submit} className="mt-2 space-y-4">
          <div>
            <label htmlFor="verify-name" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-500">
              Full name (as shown on ID)
            </label>
            <input
              id="verify-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
              data-testid="verify-name-input"
              className="w-full border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-brand-ink"
              placeholder="Jane Doe"
            />
          </div>
          <div>
            <label htmlFor="verify-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-500">
              Email for checkout
            </label>
            <input
              id="verify-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              data-testid="verify-email-input"
              className="w-full border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-brand-ink"
              placeholder="you@email.com"
            />
          </div>

          {alreadyVerified ? (
            <div className="flex items-center gap-3 bg-brand-sand p-4" data-testid="verify-already-verified">
              <BadgeCheck className="h-6 w-6 flex-shrink-0 text-green-600" />
              <p className="text-sm font-semibold text-neutral-700">
                You're already verified — no need to upload your ID again.
              </p>
            </div>
          ) : (
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-500">
                Photo of your ID (or selfie holding it)
              </label>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])}
                data-testid="verify-id-file-input"
              />
              {preview ? (
                <button type="button" onClick={() => fileRef.current?.click()} className="block w-full" data-testid="verify-id-preview">
                  <img src={preview} alt="ID preview" className="h-36 w-full border border-neutral-200 object-cover" />
                  <span className="mt-1.5 block text-center text-xs font-semibold text-neutral-500">Tap to retake</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="verify-id-upload-button"
                  className="flex w-full flex-col items-center gap-2 border border-dashed border-neutral-300 p-6 text-neutral-500 transition-colors hover:border-brand-ink hover:text-brand-ink"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold">
                    <Camera className="h-5 w-5" /> Take a photo
                  </span>
                  <span className="flex items-center gap-2 text-xs">
                    <Upload className="h-3.5 w-3.5" /> or upload from your device
                  </span>
                </button>
              )}
              <p className="mt-2 text-[11px] leading-relaxed text-neutral-400">
                Your ID is stored securely and attached to your order for compliance. Must be 21+.
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="verify-submit-button"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-lime py-4 text-sm font-bold tracking-wide text-brand-ink transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-brand-limehover disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy ? "VERIFYING..." : "VERIFY & CONTINUE TO CHECKOUT"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
